import PropTypes from 'prop-types';
import React from 'react';
import _ from 'lodash';
import classnames from 'classnames';
import Col from 'react-bootstrap/lib/Col';
import Row from 'react-bootstrap/lib/Row';
import withRouter from 'react-router/lib/withRouter';

import User from '../../records/User';
import { onEnterPress } from '../../lib/a11y';
import { translate as t } from '../../lib/I18n';
import CustomerRegistration from '../../records/customer/Registration';
import SmartcardRegistration from '../../records/customer/SmartcardRegistration';
import CustomerActions from '../../actions/CustomerActions';
import {
    AccountPages,
    AppName,
    CONSENTS,
    GlobalModals,
    RegistrationReferrers,
    SignupTypes,
    SmartcardSelfRegistrationStatuses,
} from '../../constants/AppConstants';
import { CustomerEmailInUseError } from '../../errors/Customer';
import {
    SelfSmartcardRegistrationError,
    SmartcardPostcodeError,
    SmartcardRegistrationError,
} from '../../errors/Smartcard';
import ModalActions from '../../actions/ModalActions';
import urls from '../../urls';
import UserStore from '../../stores/UserStore';
import FeatureFlagStore from '../../stores/FeatureFlagStore';
import connectToStores from '../../helpers/connectToStores';
import config from '../../config';
import DimensionsStore from '../../stores/DimensionsStore';
import { serviceUnavailable } from '../../helpers/ServiceUnavailable';
import DataLayerStore from '../../stores/DataLayerStore';

import ExclamationIcon from '../icons/ExclamationIcon';
import BaseModal from '../general/BaseModal';
import FormError from '../form/Error';
import Email from '../customer/Email';
import CustomerName from '../customer/Name';
import CustomerPassword from '../customer/Password';
import SignupOptions from '../customer/SignupOptions';
import RegisterHello from '../login/RegisterHello';
import RegisterSmartcard from './RegisterSmartcard';
import FeatureFlag from '../general/FeatureFlag';
import FormComponent from '../form/FormComponent';
import RegisterSuccessModal from './RegisterSuccessModal';
import FormattedHTMLMessage from '../general/FormattedHTMLMessage';
import BAlert from '../general/BAlert';
import WayPointWrapper from '../WayPointWrapper';
import RegisterModalFooter from './RegisterModalFooter';
import RegisterModalHeader from './RegisterModalHeader';
import StepsNavigation from '../general/StepsNavigation';
import StepDetail from '../general/StepDetail';
import DateOfBirth from '../../records/customer/DateOfBirth';
import BrandUsps from './BrandUsps';
import { SubscribeDelayRepay } from '../account/subscribeDelayRepay';
import ConsentsStore from '../../stores/ConsentsStore';
import ConsentsForm from '../../records/customer/ConsentsForm';
import ConsentsActions from '../../actions/ConsentsActions';

const VIEWPORT_BREAK_POINT_WIDTH = 992;

const REGISTER_NO_STEP_COMPLETED = 0;

export const RegisterSteps = {
    DETAILS: 1,
    SMARTCARD: 2,
    PREFERENCES: 3,
};

@withRouter
export class RegisterModal extends FormComponent {
    static propTypes = {
        user: PropTypes.instanceOf(User),
        customer: PropTypes.object,
        onRegister: PropTypes.func,
        onCancel: PropTypes.func,
        onCancelLogin: PropTypes.func,
        forceSmartcardSignup: PropTypes.bool,
        enableSmartcard: PropTypes.bool,
        enableSmartcardTransferAtSignUp: PropTypes.bool,
        enableSmartcardAtSignup: PropTypes.bool,
        enableForceScrollRegistration: PropTypes.bool,
        enableKeyGoRegistrationAtSignup: PropTypes.bool,
        enableAutoDelayRepay: PropTypes.bool,
        enableMarketingConsents: PropTypes.bool,
        isDesktopWidth: PropTypes.bool,
        isTabletCustomBreakpoint: PropTypes.bool,
        hideSmartcardCheckbox: PropTypes.bool,
        hideSmartcardPromotionalText: PropTypes.bool,
        loginTitleText: PropTypes.string,
        initialStep: PropTypes.number,
        titleText: PropTypes.string,
        onSmartcardSuccess: PropTypes.func,
        withISRN: PropTypes.bool,
        emailDefaultValue: PropTypes.string,
        onSuccessClose: PropTypes.func,
        onCloseSmartcardError: PropTypes.func,
        disableBlur: PropTypes.bool,
        disableEscape: PropTypes.bool,
        hideClose: PropTypes.bool,
        handleLogin: PropTypes.func,
        consentsForm: PropTypes.instanceOf(ConsentsForm),
    };

    static defaultProps = {
        user: null,
        customer: null,
        emailDefaultValue: null,
        onRegister: _.noop,
        onCancel: _.noop,
        onCancelLogin: _.noop,
        forceSmartcardSignup: false,
        enableSmartcard: false,
        enableSmartcardTransferAtSignUp: false,
        enableSmartcardAtSignup: true,
        enableForceScrollRegistration: false,
        enableKeyGoRegistrationAtSignup: false,
        enableAutoDelayRepay: false,
        enableMarketingConsents: false,
        isDesktopWidth: false,
        isTabletCustomBreakpoint: false,
        hideSmartcardCheckbox: true,
        hideSmartcardPromotionalText: true,
        withISRN: false,
        loginTitleText: 'account.login',
        initialStep: null,
        titleText: 'account.register',
        onSmartcardSuccess: _.noop,
        onSuccessClose: _.noop,
        disableBlur: false,
        disableEscape: false,
        hideClose: false,
        handleLogin: null,
        consentsForm: new ConsentsForm(),
    };

    state = {
        form: new CustomerRegistration({
            email: this.props.emailDefaultValue,
            referrer: RegistrationReferrers.REGISTER_MODAL,
            ...this.props.customer,
        }),
        smartcard: new SmartcardRegistration({
            signup: this.props.forceSmartcardSignup
                ? SignupTypes.WITH_SMARTCARD
                : SignupTypes.WITHOUT_SMARTCARD,
            selfRegistered: this.props.withISRN,
            isMainRegistration: true,
        }),
        badPostcode: null,
        emailInUse: null,
        furthestStep: REGISTER_NO_STEP_COMPLETED,
        isDirty: false,
        loading: false,
        scrollComplete: false,
        step: this.props.initialStep || RegisterSteps.DETAILS,
        submitted: false,
        smartcardSubmitted: false,
        isSubscribeModalOpen: false,
    };

    _trackedFields = new Set();

    componentDidMount() {
        this._trackStepViewed(this.state.step);
    }

    componentDidUpdate(prevProps, prevState) {
        if (this._shouldFocus(prevProps, prevState)) {
            _.defer(this._focusOnError);
        }

        if (prevState.step !== this.state.step) {
            this._trackStepViewed(this.state.step);
        }
    }

    _getStepName = (step) => {
        switch (step) {
            case RegisterSteps.DETAILS:
                return 'details';
            case RegisterSteps.SMARTCARD:
                return 'key_smartcard';
            case RegisterSteps.PREFERENCES:
                return 'preferences';
            default:
                return 'unknown';
        }
    };

    _trackStepViewed = (step) => {
        const stepName = this._getStepName(step);
        DataLayerStore.signupStepViewed(stepName);
    };

    _trackValidationErrors = () => {
        const { form, smartcard, step } = this.state;
        const validation = form.getValidationSummary();

        if (validation) {
            Object.keys(validation).forEach(fieldName => {
                const errorMessage = validation[fieldName];
                if (errorMessage) {
                    DataLayerStore.signupValidationError(fieldName, errorMessage);
                }
            });
        }

        if (step === RegisterSteps.SMARTCARD && smartcard.withSmartcard() && !smartcard.isComplete()) {
            const smartcardValidation = smartcard.getValidationSummary ? smartcard.getValidationSummary() : null;
            if (smartcardValidation) {
                Object.keys(smartcardValidation).forEach(fieldName => {
                    const errorMessage = smartcardValidation[fieldName];
                    if (errorMessage) {
                        DataLayerStore.signupValidationError(`smartcard_${fieldName}`, errorMessage);
                    }
                });
            }
        }
    };

    _trackAbandonment = () => {
        const { step, submitted } = this.state;
        const stepName = this._getStepName(step);
        const hasValidationError = submitted && !this._isStepValid(step);
        const hasStartedForm = this.state.isDirty;

        DataLayerStore.signupAbandoned(stepName, hasValidationError, hasStartedForm);
    };

    _handleCancel = (opts) => {
        this._trackAbandonment();
        this.props.onCancel(opts);
        ModalActions.close(GlobalModals.REGISTER);
    };

    _handleSubmission = async () => {
        const { form, smartcard, step } = this.state;
        const {
            onSmartcardSuccess,
            disableSuccessDropdown,
            withISRN,
            onSuccessClose,
            onCloseSmartcardError,
            isRegistering,
            enableMarketingConsents,
            consentsForm,
            router,
            onRegister,
        } = this.props;

        if (isRegistering) {
            return null;
        }

        if (step === RegisterSteps.SMARTCARD) {
            this.setState({ smartcardSubmitted: true });
        }

        if (!form.isComplete() || (!smartcard.isComplete() && smartcard.withSmartcard())) {
            this.setState({ submitted: true });
            this._trackValidationErrors();

            if (form.isComplete() && smartcard.withSmartcard() && !smartcard.get('addressLine1')) {
                const postcodeLookup = document.getElementById('postCodeLookUp');
                setTimeout(() => {
                    postcodeLookup.focus();
                }, 100);
            }

            return null;
        }

        this.setState({ loading: true });

        const hasSmartcard = smartcard.withSmartcard();
        DataLayerStore.signupAttempt(hasSmartcard);

        try {
            let newSmartcard = null;
            const isLoggedIn = this.props.user.isLoggedIn();

            let user = null;

            if (isLoggedIn) {
                user = this.props.user;
            } else {
                const updatedForm = FeatureFlagStore.get('enableOneClickDelayRepay') ? form.setIn(['preferences', 'autoDelayRepay'], false) : form;
                user = await CustomerActions.register(updatedForm);
            }

            if (enableMarketingConsents) {
                await ConsentsActions.updateConsentsAndUser({ form: consentsForm, user });
            }

            if (smartcard.withSmartcard()) {
                newSmartcard = withISRN
                    ? await CustomerActions.linkSmartcard(user, smartcard)
                    : await CustomerActions.registerSmartcard(user, smartcard);
            }

            DataLayerStore.signupSuccess(hasSmartcard);

            if (smartcard.withTransfer()) {
                router.push(urls.location(AccountPages.SMARTCARD_TRANSFER));
                ModalActions.close();
            } else {
                onRegister();
                ModalActions.open(RegisterSuccessModal, {
                    smartcard: !withISRN && newSmartcard,
                    disableSuccessDropdown,
                    onSuccessClose,
                    onOptInADR: FeatureFlagStore.get('enableOneClickDelayRepay') ? this.handleSubscribeModalOpen : () => null,
                });
            }

            if (onSmartcardSuccess) {
                onSmartcardSuccess();
            }
        } catch (error) {
            const state = { loading: false };
            const stepName = this._getStepName(step);

            if (error instanceof CustomerEmailInUseError) {
                state.emailInUse = form.email;
                state.step = RegisterSteps.DETAILS;
                DataLayerStore.signupBEFailure('email_in_use', stepName);
            } else if (error instanceof SmartcardPostcodeError) {
                state.badPostcode = this.state.smartcard.postcode;
                state.step = RegisterSteps.SMARTCARD;
                DataLayerStore.signupBEFailure('invalid_postcode', stepName);
            } else if (error instanceof SmartcardRegistrationError) {
                console.error('Smartcard registration error', error);
                DataLayerStore.signupBEFailure('smartcard_registration_error', stepName);
                return ModalActions.open(GlobalModals.INFO, {
                    title: 'account.register.partialSuccess',
                    message: 'account.register.partialSuccessMessage',
                    customIcon: (
                        <React.Fragment>
                            <RegisterHello
                                name={this.state.form.firstNames}
                                className="register-modal__success-message--heading"
                            />
                            <ExclamationIcon />
                        </React.Fragment>
                    ),
                    disableHeader: true,
                    disableBorderFooter: true,
                });
            } else if (error instanceof SelfSmartcardRegistrationError) {
                console.error('Smartcard self registration error', error);
                DataLayerStore.signupBEFailure('self_smartcard_registration_error', stepName);
                ModalActions.open(GlobalModals.INFO, {
                    type: 'warning',
                    title: 'account.register.selfSmartcardError',
                    message: serviceUnavailable(error.extra.error.body.status)
                        ? 'error.general.message'
                        : 'account.register.selfSmartcardErrorMessagePartial',
                    onClose: () =>
                        onCloseSmartcardError(
                            this.props.user,
                            SmartcardSelfRegistrationStatuses.NEW_SMARTCARD,
                        ),
                });
            } else {
                DataLayerStore.signupBEFailure('unknown_error', stepName);
                throw error;
            }
            this.setState(state);
        }
        return null;
    };

    _handleLogin = () => {
        const { loginTitleText, titleText, onCancel, onCancelLogin } = this.props;
        ModalActions.close(GlobalModals.REGISTER);

        if (config.get('name') !== AppName.DELAY_REPAY) {
            ModalActions.open(GlobalModals.LOGIN, {
                titleText: loginTitleText,
                registerTitleText: titleText,
                onCancel: onCancelLogin,
                onCancelRegister: onCancel,
            });
        }
    };

    _handleAgeToggled = (isOver16, updatedPreferences) => {
        const { consentsForm } = this.props;

        DataLayerStore.signupOver16Toggled();

        const form = this.state.form
            .set('preferences', updatedPreferences)
            .set('isOver16', isOver16);

        let smartcard = this.state.smartcard.set('isOver16', isOver16);

        if (!isOver16) {
            smartcard = smartcard.set('dateOfBirth', new DateOfBirth());
        }
        if (FeatureFlagStore.get('enableMarketingConsents')) {
            ConsentsActions.setConsentsForm(consentsForm.set(CONSENTS.OVER_16, isOver16));
        }
        this.setState({ smartcard, form });
    };

    _handleFieldChange = (field, value) => {
        if (!this._trackedFields.has(field)) {
            DataLayerStore.signupFieldInteraction(field);
            this._trackedFields.add(field);
        }

        let form = this.state.form.set(field, value);
        if (!form.preferences.isSomeSmsPrefSetToTrue()) {
            form = form.set('phone', null);
        }

        this.setState({
            form,
            isDirty: true,
            submitted: false,
        });
    };

    _isStepValid = step => {
        const { form, smartcard, step: currentStep } = this.state;
        let isValid = false;

        step = step ? step : currentStep;

        switch (step) {
            case RegisterSteps.DETAILS:
                isValid = form.arePersonalDetailsValid();
                break;

            case RegisterSteps.SMARTCARD:
                isValid = smartcard.isComplete();
                break;

            case RegisterSteps.PREFERENCES:
                isValid = form.isComplete();
                break;
        }

        return isValid;
    };

    _handleSmartcardUpdate = (field, value) => {
        let { form, smartcard } = this.state;
        const { consentsForm } = this.props;

        const trackingKey = `smartcard_${field}`;
        if (field !== 'signup' && !this._trackedFields.has(trackingKey)) {
            DataLayerStore.signupFieldInteraction(trackingKey);
            this._trackedFields.add(trackingKey);
        }

        if (field === 'signup') {
            if (value === SignupTypes.WITH_SMARTCARD) {
                DataLayerStore.signupSmartcardFlowStarted();
            } else if (value === SignupTypes.WITHOUT_SMARTCARD) {
                DataLayerStore.signupSmartcardFlowSkipped();
            }
        }

        if (field === 'dateOfBirth') {
            if (value.isOverAge(16)) {
                smartcard = smartcard.set('isOver16', true);
                form = form.set('isOver16', true);
                if (FeatureFlagStore.get('enableMarketingConsents')) {
                    ConsentsActions.setConsentsForm(consentsForm.set(CONSENTS.OVER_16, true));
                }
            }
        }

        if (this.props.enableAutoDelayRepay) {
            if (field === 'signup') {
                smartcard = smartcard.set('autoDrOptedIn', value === SignupTypes.WITH_SMARTCARD);
                form = form.setIn(
                    ['preferences', 'autoDelayRepay'],
                    value === SignupTypes.WITH_SMARTCARD,
                );
            }
        }

        this.setState({
            form,
            smartcard: smartcard.set(field, value),
            submitted: false,
        });
    };

    _handleSmartcardAddressSelected = address => {
        const updatedSmartcard = this.state.smartcard.merge({
            addressLine1: address.get('line1'),
            addressLine2: address.get('line2'),
            addressLine3: address.get('line3'),
            townCity: address.get('town'),
            postcode: address.get('postCode'),
        });
        this.setState({
            smartcard: updatedSmartcard,
            submitted: false,
        });
    };

    _renderComponent(Component, options = {}) {
        const { form, submitted } = this.state;
        return (
            <Component
                form={form}
                onChange={this._handleFieldChange}
                visited={submitted}
                {...options}
            />
        );
    }

    _renderEmailError() {
        const { emailInUse, form } = this.state;
        if (emailInUse && emailInUse === form.email) {
            return (
                <Col md={12}>
                    <BAlert className="register-step__form-error" type="danger">
                        <FormattedHTMLMessage
                            message="alertmessage.registration.failed.emailInUse"
                            learnMoreUrl={config.get('yourAccountHelpUrl')}
                        />
                    </BAlert>
                </Col>
            );
        }
        return null;
    }

    _handleScrollComplete = () => {
        this.setState({ scrollComplete: true });
    };

    _scrollModalBodyToTop = () => {
        const modalBody = document.querySelector('.modal-body');

        if (modalBody) {
            modalBody.scrollTop = 0;
        }
    };

    _handleContinueRegistration = event => {
        event?.preventDefault();

        const { enableSmartcard, enableSmartcardAtSignup } = this.props;
        const { furthestStep, step } = this.state;

        let nextStep = step;
        const isCurrentStepValid = this._isStepValid();

        if (isCurrentStepValid) {
            const stepName = this._getStepName(step);
            DataLayerStore.signupStepCompleted(stepName);

            switch (step) {
                case RegisterSteps.DETAILS:
                    if (enableSmartcard && enableSmartcardAtSignup) {
                        nextStep = RegisterSteps.SMARTCARD;
                    } else {
                        nextStep = RegisterSteps.PREFERENCES;
                    }
                    break;

                case RegisterSteps.SMARTCARD:
                    nextStep = RegisterSteps.PREFERENCES;
                    break;
            }
        } else {
            nextStep = null;
        }

        if (nextStep !== null) {
            this.setState({
                step: nextStep,
                submitted: false,
            });

            this._scrollModalBodyToTop();

            if (nextStep > furthestStep) {
                this.setState({ furthestStep: nextStep });
            }
        } else {
            this._handleSubmission();
        }
    };

    _handleStep = stepNumber => () => {
        const { furthestStep, step } = this.state;

        const areStepsInbetweenValid = _.range(1, stepNumber).every(this._isStepValid);

        // A user might have invaliated an earlier visited step
        // so check again before proceeding to the next one.
        if (stepNumber > step && !areStepsInbetweenValid) {
            this._handleSubmission();
        } else {
            this.setState({ step: stepNumber });

            if (stepNumber > furthestStep) {
                this.setState({ furthestStep: stepNumber - 1 });
            }

            this._scrollModalBodyToTop();
        }
    };

    _handleStepBack = stepNumber => {
        this.setState({ step: stepNumber });
        this._scrollModalBodyToTop();
    };

    handleSubscribeModalOpen = () => {
        ModalActions.open(SubscribeDelayRepay, {
            user: this.props.user,
            modalOpen: true,
            optedInFrom: 'new-registration',
            handleClose: () => {
                ModalActions.close();
            },
            shouldRedirectToAccountScreen: true,
        });
        this.setState({ isSubscribeModalOpen: true });
    };

    render() {
        const {
            enableSmartcard,
            enableSmartcardTransferAtSignUp,
            enableSmartcardAtSignup,
            enableForceScrollRegistration,
            enableKeyGoRegistrationAtSignup,
            hideSmartcardCheckbox,
            hideSmartcardPromotionalText,
            isRegistering,
            isDesktopWidth,
            isTabletCustomBreakpoint,
            titleText,
            hideClose,
            handleLogin,
            consentsForm,
        } = this.props;
        const {
            badPostcode,
            emailInUse,
            form,
            furthestStep,
            isDirty,
            loading,
            smartcard,
            step,
            submitted,
            smartcardSubmitted,
        } = this.state;
        const validation = form.getValidationSummary();

        const emailErrorProp =
            emailInUse && emailInUse === form.email
                ? { error: t('account.register.emailInUse'), autoFocus: true }
                : null;
        const hideRegisterSteps = (!enableSmartcard || !enableSmartcardAtSignup) ? ['SMARTCARD'] : [];

        const componentOptions = {
            validation,
            smartcard,
            visited: submitted,
            onAgeToggle: this._handleAgeToggled,
            vertical: true,
            outsideLabel: true,
            noTandC: true,
            noOptDescription: true,
            noPreferenceNotice: false,
        };

        const modalProps = {
            testId: 'register-modal',
            className: 'modal--register',
            closeText: 'button.text.cancel',
            continueText: 'account.register',
            disableContinue:
                loading || (enableForceScrollRegistration && !this.state.scrollComplete),
            disableEscape: true,
            footer: isTabletCustomBreakpoint && (
                <RegisterModalFooter
                    handleContinueRegistration={this._handleContinueRegistration}
                    handleSubmission={this._handleSubmission}
                    isFirstStep={step === RegisterSteps.DETAILS}
                    isSmartcardStep={step === RegisterSteps.SMARTCARD}
                    isLastStep={step === RegisterSteps.PREFERENCES}
                    isRegistering={isRegistering}
                    onSmartcardUpdate={this._handleSmartcardUpdate}
                    onClose={this._handleCancel}
                    handleLogin={handleLogin}
                    consentsForm={consentsForm}
                    isOver16={form.get('isOver16')}
                />
            ),
            header: (
                <RegisterModalHeader
                    furthestStep={furthestStep}
                    handleLogin={handleLogin || this._handleLogin}
                    handleStep={this._handleStep}
                    handleStepBack={this._handleStepBack}
                    isFormDirty={isDirty}
                    hideClose={hideClose}
                    onClose={this._handleCancel}
                    onClosePress={onEnterPress(this._handleCancel)}
                    showExtraOptions={step === RegisterSteps.DETAILS}
                    step={step}
                    dictionaryPrefix="account.register.steps.back."
                    hideSteps={hideRegisterSteps}
                    steps={RegisterSteps}
                />
            ),
            headerClass: 'modal__header',
            isAutofocusing: false,
            onEnterContinue: false,
            titleText,
            useFlex: true,
        };

        return (
            <BaseModal {...modalProps}>
                <div className="content-wrapper">
                    {isDesktopWidth && (
                        <div className="content-wrapper__usps">
                            <BrandUsps
                                steps={RegisterSteps}
                                currentStep={step}
                                dictionaryPrefix="account.register."
                            />
                        </div>
                    )}
                    <div className="content-wrapper__form">
                        <StepsNavigation
                            currentStep={step}
                            furthestStep={furthestStep}
                            dictionaryPrefix="account.register.steps."
                            onClick={this._handleStep}
                            steps={RegisterSteps}
                            hideSteps={hideRegisterSteps}
                        />
                        {!isDesktopWidth && (
                            <StepDetail
                                currentStep={step}
                                steps={RegisterSteps}
                                hideSteps={hideRegisterSteps}
                                stepTitleTagName="h1"
                                dictionaryPrefix="account.register."
                            />
                        )}
                        <section className="register-wrapper" ref={this.captureFormRef}>
                            <Row>
                                <Col xs={12}>
                                    <div
                                        className={classnames('register-step register-details', {
                                            'register-step--active': step === RegisterSteps.DETAILS,
                                        })}
                                        data-test-id="register-details-step-root"
                                    >
                                        <form onSubmit={this._handleContinueRegistration}>
                                            <input type="submit" className="hidden" />
                                            <div className="register-personal">
                                                <div className="register-personal__personal-info">
                                                    {!badPostcode && (
                                                        <React.Fragment>
                                                            <Row>{this._renderEmailError()}</Row>
                                                            <Row>
                                                                {this._renderComponent(
                                                                    CustomerName,
                                                                    componentOptions,
                                                                )}
                                                            </Row>
                                                        </React.Fragment>
                                                    )}

                                                    {!badPostcode && (
                                                        <React.Fragment>
                                                            <Row>
                                                                <Col md={12}>
                                                                    <Email
                                                                        {...emailErrorProp}
                                                                        form={form}
                                                                        onChange={
                                                                            this
                                                                                ._handleFieldChange
                                                                        }
                                                                        validation={validation}
                                                                        visited={submitted}
                                                                        label={t(
                                                                            'customerdetails.email',
                                                                        )}
                                                                        outsideLabel
                                                                    />
                                                                </Col>
                                                                <Col
                                                                    md={12}
                                                                    className="margin-bottom"
                                                                >
                                                                    <CustomerPassword
                                                                        form={form}
                                                                        field="password"
                                                                        onChange={
                                                                            this
                                                                                ._handleFieldChange
                                                                        }
                                                                        showPasswordIcon
                                                                        showPasswordHints
                                                                        validation={validation}
                                                                        visited={submitted}
                                                                        outsideLabel
                                                                    />
                                                                </Col>
                                                            </Row>
                                                        </React.Fragment>
                                                    )}
                                                </div>
                                            </div>
                                        </form>
                                    </div>

                                    <div
                                        className={classnames('register-step register-smartcard', {
                                            'register-step--active':
                                                step === RegisterSteps.SMARTCARD,
                                        })}
                                        data-test-id="register-smartcard-step-root"
                                    >
                                        {badPostcode && (
                                            <FormError
                                                message={t(
                                                    'alertmessage.registration.failed.postcodeInvalid',
                                                )}
                                            />
                                        )}

                                        <FeatureFlag name="enableSmartcard">
                                            <FeatureFlag name="enableSmartcardAtSignup">
                                                <RegisterSmartcard
                                                    ref="smartcard"
                                                    smartcard={smartcard}
                                                    onChange={this._handleSmartcardUpdate}
                                                    onAgeToggle={this._handleAgeToggled}
                                                    onSelectAddress={
                                                        this._handleSmartcardAddressSelected
                                                    }
                                                    user={this.state.form}
                                                    badPostcode={badPostcode}
                                                    enableSmartcardTransferAtSignUp={
                                                        enableSmartcardTransferAtSignUp
                                                    }
                                                    enableKeyGoRegistrationAtSignup={
                                                        enableKeyGoRegistrationAtSignup
                                                    }
                                                    hideSmartcardCheckbox={hideSmartcardCheckbox}
                                                    hideSmartcardPromotionalText={
                                                        hideSmartcardPromotionalText
                                                    }
                                                    submitted={submitted}
                                                    smartcardSubmitted={smartcardSubmitted}
                                                />
                                            </FeatureFlag>
                                        </FeatureFlag>
                                    </div>

                                    <div
                                        className={classnames(
                                            'register-step register-preferences',
                                            {
                                                'register-step--active':
                                                    step === RegisterSteps.PREFERENCES,
                                            },
                                        )}
                                        data-test-id="register-preferences-step-root"
                                    >
                                        {!badPostcode && (
                                            <Row>
                                                {this._renderComponent(
                                                    SignupOptions,
                                                    componentOptions,
                                                )}
                                                {isTabletCustomBreakpoint && t('preference-centre.privacy-notice').length > 1 && (
                                                    <Col md={12}>
                                                        <div className="margin-top--sm margin-bottom">
                                                            <FormattedHTMLMessage
                                                                className="text-sm"
                                                                message="preference-centre.privacy-notice"
                                                            />
                                                        </div>
                                                    </Col>
                                                )}
                                            </Row>
                                        )}
                                    </div>

                                    <WayPointWrapper onEnter={this._handleScrollComplete} />

                                    {!isTabletCustomBreakpoint && (
                                        <RegisterModalFooter
                                            isDesktop
                                            handleContinueRegistration={
                                                this._handleContinueRegistration
                                            }
                                            handleSubmission={this._handleSubmission}
                                            handleLogin={handleLogin || this._handleLogin}
                                            isFormDirty={isDirty}
                                            isFirstStep={step === RegisterSteps.DETAILS}
                                            isSmartcardStep={step === RegisterSteps.SMARTCARD}
                                            isLastStep={step === RegisterSteps.PREFERENCES}
                                            isRegistering={isRegistering}
                                            onClose={this._handleCancel}
                                            onSmartcardUpdate={this._handleSmartcardUpdate}
                                            consentsForm={consentsForm}
                                            isOver16={form.get('isOver16')}
                                        />
                                    )}
                                </Col>
                            </Row>
                        </section>
                    </div>
                </div>
            </BaseModal>
        );
    }
}

function _getStateFromStores(props) {
    return {
        user: UserStore.getCurrent(),
        forceSmartcardSignup:
            props.forceSmartcardSignup || FeatureFlagStore.get('enableDefaultSmartcardAtSignup'),
        enableSmartcard: FeatureFlagStore.get('enableSmartcard'),
        enableSmartcardTransferAtSignUp: FeatureFlagStore.get('enableSmartcardTransferAtSignUp'),
        enableSmartcardAtSignup: FeatureFlagStore.get('enableSmartcardAtSignup'),
        enableForceScrollRegistration: FeatureFlagStore.get('enableForceScrollRegistration'),
        enableKeyGoRegistrationAtSignup: FeatureFlagStore.get('enableKeyGoRegistrationAtSignup'),
        enableAutoDelayRepay: FeatureFlagStore.get('enableAutoDelayRepay'),
        enableMarketingConsents: FeatureFlagStore.get('enableMarketingConsents'),
        isDesktopWidth: DimensionsStore.isDesktopWidth(),
        isTabletCustomBreakpoint:
            DimensionsStore.getWindowSize().get('width') <= VIEWPORT_BREAK_POINT_WIDTH,
        isRegistering: UserStore.getIsRegistering(),
        consentsForm: ConsentsStore.form,
    };
}

export default connectToStores(
    [UserStore, FeatureFlagStore, DimensionsStore, ConsentsStore],
    _getStateFromStores,
)(RegisterModal);
